import { HTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { ReorderIcon } from '../icons';
import { tokens } from '../theme/tokens';
import { Card } from './Card';
import { Spinner } from './Spinner';

const selectedRing = `0 0 0 2px ${tokens.text.primary}`;

const Row = styled.div<{ $dragging?: boolean }>`
	display: flex;
	align-items: stretch;
	width: 100%;
	box-sizing: border-box;
	border-radius: ${tokens.radius.md}px ${tokens.radius.md}px ${tokens.radius.md}px
		${tokens.radius.sm}px;

	&:has([data-drag-handle]:hover:not([data-inactive])),
	&:has([data-drag-handle][data-preview='hover-handle']:not([data-inactive])) {
		box-shadow: ${tokens.shadow.xs};
	}

	&[data-selected]:has([data-drag-handle]:hover:not([data-inactive])),
	&[data-selected]:has([data-drag-handle][data-preview='hover-handle']:not([data-inactive])) {
		box-shadow: ${selectedRing};

		[data-drag-handle] {
			border-color: transparent;
		}
	}

	${(props) =>
		props.$dragging &&
		css`
			transform: rotate(-2deg);
			box-shadow: ${selectedRing}, ${tokens.shadow.lg};

			[data-drag-handle] {
				border-color: transparent;
			}
		`}
`;

const Handle = styled.div<{ $dragging?: boolean; $inactive?: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 34px;
	margin-right: -1px;
	box-sizing: border-box;
	border: 1px solid transparent;
	border-radius: ${tokens.radius.md}px 0 0 ${tokens.radius.sm}px;
	color: ${tokens.icon.tertiary};
	cursor: grab;
	z-index: 1;

	&:hover,
	&[data-preview='hover-handle'] {
		background: ${tokens.surface.secondary};
		border-color: ${tokens.border.subtle};
		color: ${tokens.icon.primary};
	}

	${(props) =>
		props.$inactive &&
		css`
			cursor: default;

			&:hover,
			&[data-preview='hover-handle'] {
				background: none;
				border-color: transparent;
				color: ${tokens.icon.tertiary};
			}
		`}

	${(props) =>
		props.$dragging &&
		css`
			background: ${tokens.surface.sidebar};
			border-color: ${tokens.border.focus};
			color: ${tokens.text.inverse};
			cursor: grabbing;
		`}
`;

const HandleControl = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 32px;
	height: 32px;
	padding: ${tokens.padding.compact}px;
	box-sizing: border-box;
	border-radius: ${tokens.radius.full}px;

	svg {
		display: block;
		width: 16px;
		height: 16px;
		color: inherit;
	}
`;

const connectedSlot = css`
	[data-slot],
	[data-slot][data-selected] {
		border-top-left-radius: 0;
		border-bottom-left-radius: 0;
		box-shadow: none;
	}
`;

const slotLayout = css<{ $dragging?: boolean; $interactive?: boolean }>`
	display: flex;
	flex: 1 1 auto;
	min-width: 0;

	${(props) => props.$interactive && `cursor: pointer;`}

	[data-slot] {
		flex: 1 1 auto;
		width: 100%;
		min-width: 0;
	}

	[data-slot]:hover,
	[data-slot][data-preview='hover-card'] {
		background: ${tokens.surface.hover};
	}

	[data-slot]:hover:not([data-selected]),
	[data-slot][data-preview='hover-card']:not([data-selected]) {
		box-shadow: ${tokens.shadow.xs};
	}

	[data-slot][data-selected]:hover,
	[data-slot][data-selected][data-preview='hover-card'] {
		box-shadow: ${selectedRing};
	}

	${Handle}:hover:not([data-inactive]) + &,
	${Handle}[data-preview='hover-handle']:not([data-inactive]) + & {
		${connectedSlot}
	}

	${(props) => props.$dragging && connectedSlot}
`;

const SlotFrame = styled.div<{ $dragging?: boolean; $interactive?: boolean }>`
	${slotLayout}
`;

const SlotLink = styled(Link)<{ $dragging?: boolean; $interactive?: boolean }>`
	${slotLayout}
	color: inherit;
	text-decoration: none;
`;

const SlotBody = styled.div`
	display: flex;
	align-items: center;
	gap: ${tokens.gap.section}px;
	min-width: 0;
	min-height: 64px;
	box-sizing: border-box;
	padding: ${tokens.padding.container}px;
`;

export function DraggableCard({
	children,
	dragHandleProps,
	isDragging,
	isDragDisabled,
	loading,
	to,
	toState,
	selected,
	onClick,
	'data-preview': dataPreview,
	...rest
}: {
	children: ReactNode;
	dragHandleProps?: HTMLAttributes<HTMLElement>;
	isDragging?: boolean;
	isDragDisabled?: boolean;
	loading?: boolean;
	to?: string;
	toState?: object;
	selected?: boolean;
	onClick?: () => void;
	'data-preview'?: 'hover-handle' | 'hover-card' | 'dragging';
} & Omit<HTMLAttributes<HTMLDivElement>, 'onClick' | 'title'>) {
	const dragging = isDragging || dataPreview === 'dragging';
	const showHandle = !isDragDisabled;
	const handleInactive = showHandle && !dragHandleProps && !dragging && !loading;
	const interactive = !!to || !!onClick;
	const [pathname, params] = to?.split('?') ?? [];

	const slot = (
		<Card
			p={0}
			selected={selected}
			data-slot=""
			data-selected={selected ? '' : undefined}
			data-preview={dataPreview === 'hover-card' ? 'hover-card' : undefined}
		>
			<SlotBody>{children}</SlotBody>
		</Card>
	);

	return (
		<Row $dragging={dragging} data-selected={selected ? '' : undefined} {...rest}>
			{showHandle && (
				<Handle
					{...(loading || handleInactive ? undefined : dragHandleProps)}
					$dragging={dragging}
					$inactive={handleInactive}
					data-drag-handle=""
					data-inactive={handleInactive ? '' : undefined}
					data-preview={dataPreview === 'hover-handle' ? 'hover-handle' : undefined}
				>
					<HandleControl>{loading ? <Spinner /> : <ReorderIcon size={16} />}</HandleControl>
				</Handle>
			)}
			{to ? (
				<SlotLink
					$dragging={dragging}
					$interactive={interactive}
					to={{ pathname, search: params && `?${params}`, state: toState }}
				>
					{slot}
				</SlotLink>
			) : (
				<SlotFrame $dragging={dragging} $interactive={interactive} onClick={onClick}>
					{slot}
				</SlotFrame>
			)}
		</Row>
	);
}
